import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, Tag, Empty, Spin, Descriptions, Typography, Popconfirm, Space, Select, Input, message } from 'antd';
import { PlusOutlined, LoginOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation, LANGUAGES } from '../../../shared/i18n';
import apiClient from '../../../shared/services/apiClient';
import { userPath } from '../../../shared/config/paths';

const { Title, Text } = Typography;

const STATE = {
    provisioning: ['processing', 'Activating'],
    installing: ['processing', 'Installing'],
    ready: ['success', 'Running'],
    failed: ['error', 'Failed'],
    suspended: ['warning', 'Suspended'],
    removed: ['default', 'Uninstalled'],
};

const CDN_STATE = {
    connecting: ['processing', 'Connecting'],
    dns: ['warning', 'Waiting for DNS'],
    active: ['success', 'Active'],
    failed: ['error', 'Failed'],
};

/** 自动接入的 CDN:DNS 不在我们这里时显示要设的 CNAME */
function CdnState({ cdn, t }) {
    if (cdn.state === 'off') return <Text type="secondary">{t('Not connected')}</Text>;
    const [color, label] = CDN_STATE[cdn.state] || ['default', cdn.state];
    return (
        <Space direction="vertical" size={4}>
            <Space wrap>
                <Tag color={color}>{t(label)}</Tag>
                {cdn.cdn_domain && <Link to={userPath(`/cdn/domains/${cdn.cdn_domain}`)}>{t('CDN settings')}</Link>}
            </Space>
            {cdn.state === 'dns' && cdn.cname && (
                <Text type="secondary">
                    {t('Add a CNAME record:')} <Text code>{cdn.hostname}</Text> → <Text code copyable>{cdn.cname}</Text>
                </Text>
            )}
            {cdn.state === 'dns' && cdn.www && cdn.www_cname && (
                <Text type="secondary">
                    {t('Add a CNAME record:')} <Text code>{cdn.www}</Text> → <Text code copyable>{cdn.www_cname}</Text>
                </Text>
            )}
        </Space>
    );
}

/** 虚拟主机 → WordPress:WordPress 套餐的站点,后台地址 / 用户名 / 密码(平台保管) */
export default function WordpressSites() {
    const { t } = useTranslation();
    const [sites, setSites] = useState(null);
    const [passwords, setPasswords] = useState({});
    const [busy, setBusy] = useState({});
    const [edits, setEdits] = useState({}); // vhost_id → {lang, admin_email} 未保存的修改

    const load = useCallback(async () => {
        try {
            const r = await apiClient.get('/api/user/wordpress/sites');
            setSites(r.data?.data?.sites || []);
        } catch (e) {
            setSites((s) => s || []);
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    const pending = (sites || []).some((s) => ['provisioning', 'installing'].includes(s.state) || s.password_busy || s.cdn?.state === 'connecting' || s.settings?.busy);
    useEffect(() => {
        if (!pending) return undefined;
        const id = setInterval(load, 10000);
        return () => clearInterval(id);
    }, [pending, load]);

    const showPassword = async (s) => {
        try {
            const r = await apiClient.get(`/api/user/wordpress/sites/${s.vhost_id}/password`);
            setPasswords((p) => ({ ...p, [s.vhost_id]: r.data?.data?.password }));
        } catch (e) {
            message.error(e?.error || e?.message || t('Operation failed'));
        }
    };

    const resetPassword = async (s) => {
        setBusy((b) => ({ ...b, [s.vhost_id]: true }));
        try {
            await apiClient.post(`/api/user/wordpress/sites/${s.vhost_id}/reset-password`);
            setPasswords((p) => ({ ...p, [s.vhost_id]: undefined }));
            message.success(t('Resetting password…'));
            load();
        } catch (e) {
            message.error(e?.error || e?.message || t('Operation failed'));
        } finally {
            setBusy((b) => ({ ...b, [s.vhost_id]: false }));
        }
    };

    // 一次性登录链接(60 秒):先同步开窗口,免得被浏览器当弹窗拦掉
    const login = async (s) => {
        const w = window.open('', '_blank');
        setBusy((b) => ({ ...b, [`login${s.vhost_id}`]: true }));
        try {
            const r = await apiClient.post(`/api/user/wordpress/sites/${s.vhost_id}/login`);
            const url = r.data?.data?.url;
            if (w && url) w.location.href = url; else if (url) window.location.href = url;
        } catch (e) {
            if (w) w.close();
            message.error(e?.error || e?.message || t('Operation failed'));
        } finally {
            setBusy((b) => ({ ...b, [`login${s.vhost_id}`]: false }));
        }
    };

    // 站点语言 / 管理员邮箱:节点上用 wp-cli 改,完成前显示处理中
    const saveSettings = async (s, v) => {
        setBusy((b) => ({ ...b, [`set${s.vhost_id}`]: true }));
        try {
            await apiClient.post(`/api/user/wordpress/sites/${s.vhost_id}/settings`, { lang: v.lang, admin_email: v.admin_email.trim() });
            setEdits((e) => ({ ...e, [s.vhost_id]: undefined }));
            load();
        } catch (e) {
            message.error(e?.error || e?.message || t('Operation failed'));
        } finally {
            setBusy((b) => ({ ...b, [`set${s.vhost_id}`]: false }));
        }
    };

    const buy = (
        <Link to={userPath('/provision/host_shared?variant=wordpress')}>
            <Button type="primary" icon={<PlusOutlined />}>{t('Buy')}</Button>
        </Link>
    );

    return (
        <div style={{ padding: 16 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>WordPress</Title>
                {buy}
            </Space>
            {sites === null ? <Spin /> : sites.length === 0 ? <Card><Empty>{buy}</Empty></Card> : sites.map((s) => {
                const [color, label] = STATE[s.state] || ['default', s.state];
                const ins = s.install;
                const ready = s.state === 'ready' && ins;
                const cur = s.settings;
                const v = cur ? { ...cur, ...(edits[s.vhost_id] || {}) } : null;
                const edit = (k, val) => setEdits((e) => ({ ...e, [s.vhost_id]: { ...(e[s.vhost_id] || {}), [k]: val } }));
                const dirty = cur && (v.lang !== cur.lang || v.admin_email.trim().toLowerCase() !== cur.admin_email);
                return (
                    <Card key={s.subscription_id} style={{ marginBottom: 16 }}
                        title={<Space>{ready ? <a href={ins.site_url} target="_blank" rel="noopener noreferrer">{s.domain}</a> : s.domain}<Tag color={color}>{t(label)}</Tag></Space>}
                        extra={s.vhost_id ? <Link to={userPath(`/host_shared/vhosts/${s.vhost_id}`)}>{t('Manage hosting')}</Link> : null}>
                        {ready ? (
                            <Descriptions column={1} size="small">
                                <Descriptions.Item label={t('Admin URL')}>
                                    <Space wrap>
                                        <Text copyable>{ins.admin_url}</Text>
                                        {s.login_ready
                                            ? <Button size="small" type="primary" icon={<LoginOutlined />} loading={busy[`login${s.vhost_id}`]} onClick={() => login(s)}>{t('Log in')}</Button>
                                            : <Button size="small" icon={<LoginOutlined />} href={ins.admin_url} target="_blank" rel="noopener noreferrer">{t('Open')}</Button>}
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Username')}><Text copyable>{ins.admin_user}</Text></Descriptions.Item>
                                <Descriptions.Item label={t('Password')}>
                                    <Space wrap>
                                        {passwords[s.vhost_id]
                                            ? <Text copyable code>{passwords[s.vhost_id]}</Text>
                                            : <Button size="small" icon={<EyeOutlined />} disabled={s.password_busy} onClick={() => showPassword(s)}>{t('Show')}</Button>}
                                        <Popconfirm title={t('Generate a new admin password?')} onConfirm={() => resetPassword(s)}>
                                            <Button size="small" icon={<ReloadOutlined />} loading={busy[s.vhost_id] || s.password_busy}>{t('Reset password')}</Button>
                                        </Popconfirm>
                                    </Space>
                                </Descriptions.Item>
                                {cur && <Descriptions.Item label={t('Site language')}>
                                    <Select size="small" style={{ minWidth: 160 }} value={v.lang} onChange={(x) => edit('lang', x)} disabled={cur.busy}
                                        options={LANGUAGES.map((l) => ({ value: l.code, label: l.label }))} />
                                </Descriptions.Item>}
                                {cur && <Descriptions.Item label={t('Admin email')}>
                                    <Space wrap>
                                        <Input size="small" style={{ width: 240 }} value={v.admin_email} onChange={(e) => edit('admin_email', e.target.value)} disabled={cur.busy} />
                                        {(dirty || cur.busy) && <Button size="small" type="primary" loading={cur.busy || busy[`set${s.vhost_id}`]} onClick={() => saveSettings(s, v)}>{t('Save')}</Button>}
                                        {cur.error && !cur.busy && !dirty && <Text type="danger">{t('Last change failed')}</Text>}
                                    </Space>
                                </Descriptions.Item>}
                                <Descriptions.Item label={t('Version')}>{ins.version}</Descriptions.Item>
                                {s.cdn && <Descriptions.Item label="CDN"><CdnState cdn={s.cdn} t={t} /></Descriptions.Item>}
                            </Descriptions>
                        ) : (
                            <Text type="secondary">
                                {s.state === 'failed' ? t('Installation failed. We have been notified and will fix it.')
                                    : s.state === 'removed' ? t('WordPress was uninstalled from this site.')
                                        : s.state === 'suspended' ? t('This site is suspended')
                                            : t('WordPress is being installed. This usually takes a few minutes.')}
                            </Text>
                        )}
                    </Card>
                );
            })}
        </div>
    );
}
